//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Paper, Tab } from '@mui/material'
import LoanList from '@react/components/loans/LoanList'
import PoolContractAddresses from '@react/components/pools/PoolContractAddresses'
import PoolLegalPage from '@react/components/pools/PoolLegalPage'
import LenderPoolActionModals from '@react/lender/LenderPoolActionModals'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LenderPoolHoldings from './LenderPoolHoldings'
import LenderPoolInvestmentSummary from './LenderPoolInvestmentSummary'
import PoolOverview from './PoolOverview'

import CollateralView from '@react/admin/CollateralView'
import { PoolFlexCalendar } from '@react/components/pools/PoolFlexCalendat'
import PoolDetailsSkeleton from '@react/components/skeletons/PoolDetailsSkeleton'
import { OtStoreState, useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { displayName } from '@shared/PoolBaseCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

export enum PoolTabEnum {
  OVERVIEW = 'OVERVIEW',
  HOLDINGS = 'HOLDINGS',
  ACCESS_LIST = 'ACCESS_LIST',
  DEPOSITS = 'DEPOSITS',
  WITHDRAWS = 'WITHDRAWS',
  LENDER_VIEW = 'LENDER_VIEW',
  SCHEDULE = 'SCHEDULE',
  LEGAL = 'LEGAL',
  ASSETS = 'ASSETS',
  LOANS = 'LOANS',
  WITHDRAW_EVENTS = 'WITHDRAW_EVENTS',
  CONTRACT_ADDRESSES = 'CONTRACT_ADDRESSES',
  FLEX_CALENDAR = 'FLEX_CALENDAR',
  OFFCHAIN_DATA = 'OFFCHAIN_DATA',
  REPORTS = 'REPORTS',
}
export default function LenderPoolDetails(props: { poolAddr?: EthAddr }): ReactElement {
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = React.useState<PoolTabEnum>(PoolTabEnum.HOLDINGS)
  const handleChange = (_event: React.SyntheticEvent, newValue: PoolTabEnum): void => {
    setSelectedTab(newValue)
  }
  const poolBaseOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  )

  const allPools = useOtSelector((state: OtStoreState) => state.poolState.poolOverviewCTOs)

  useEffect(() => {
    if (poolAddr && !poolBaseOverviewCTO && Object.keys(allPools).length > 0) {
      logOT.info('location navigate to /')
      navigate('/')
    }
  }, [poolAddr, poolBaseOverviewCTO, allPools])

  if (!poolAddr || !poolBaseOverviewCTO) {
    return <PoolDetailsSkeleton />
  }

  return (
    <Box>
      <LenderPoolActionModals poolName={displayName(poolBaseOverviewCTO)} poolAddr={poolAddr} />
      <LenderPoolInvestmentSummary poolAddr={poolAddr} />
      <Paper sx={{ marginTop: 4, marginBottom: 4 }}>
        <Box sx={{ width: '100%' }}>
          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleChange}
                aria-label="lab API tabs example"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={'My Investments'} value={PoolTabEnum.HOLDINGS} />
                <Tab label={'Overview'} value={PoolTabEnum.OVERVIEW} />
                {poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term && (
                  <Tab label={'Loans'} value={PoolTabEnum.LOANS} />
                )}
                <Tab label={'Collateral'} value={PoolTabEnum.ASSETS} />
                <Tab label={'Info'} value={PoolTabEnum.LEGAL} />
                <Tab label={'Contract Addresses'} value={PoolTabEnum.CONTRACT_ADDRESSES} />
                {poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate && (
                  <Tab label={'Calendar'} value={PoolTabEnum.FLEX_CALENDAR} />
                )}
              </TabList>
            </Box>
            <TabPanel value={PoolTabEnum.HOLDINGS}>
              <LenderPoolHoldings />
            </TabPanel>
            <TabPanel value={PoolTabEnum.OVERVIEW}>
              <PoolOverview />
            </TabPanel>
            <TabPanel value={PoolTabEnum.LEGAL}>
              <PoolLegalPage />
            </TabPanel>
            {poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term && (
              <TabPanel value={PoolTabEnum.LOANS}>
                <LoanList />
              </TabPanel>
            )}
            <TabPanel value={PoolTabEnum.ASSETS}>
              <CollateralView poolAddr={poolAddr} readOnly={true}></CollateralView>
            </TabPanel>
            <TabPanel value={PoolTabEnum.CONTRACT_ADDRESSES}>
              <PoolContractAddresses role={RoleEnum.LENDER} />
            </TabPanel>
            <TabPanel value={PoolTabEnum.FLEX_CALENDAR}>
              <PoolFlexCalendar poolAddr={poolAddr} role={RoleEnum.LENDER} />
            </TabPanel>
          </TabContext>
        </Box>
      </Paper>
    </Box>
  )
}
